import { useAtlasStore } from '@/stores/atlasStore';
import { LAYER_LEGENDS, type LegendDef } from '@/config/legends';

function GradientLegend({ def, min, max }: { def: LegendDef; min?: string; max?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="h-3 w-24 rounded-sm" style={{ background: def.gradient }} />
      <div className="flex justify-between text-[9px] font-mono text-clay">
        <span>{min ?? '?'}</span>
        <span>{max ?? '?'}{def.unit ? ` ${def.unit}` : ''}</span>
      </div>
    </div>
  );
}

function DiscreteLegend({ def }: { def: LegendDef }) {
  if (!def.classes) return null;
  const cols = def.classes.length > 5 ? 2 : 1;
  return (
    <div className={`grid gap-x-3 gap-y-0.5 ${cols === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {def.classes.map((c) => (
        <div key={c.label} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
          <span className="text-[9px] text-clay whitespace-nowrap">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function LineLegend({ def }: { def: LegendDef }) {
  if (!def.classes) return null;
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
      {def.classes.map((c) => (
        <div key={c.label} className="flex items-center gap-1.5">
          <span
            className="flex-shrink-0 rounded-full"
            style={{ backgroundColor: c.color, width: 16, height: Math.max(2, c.width || 2) }}
          />
          <span className="text-[9px] text-clay">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function FillLegend({ def }: { def: LegendDef }) {
  if (!def.classes) return null;
  return (
    <div className={`grid gap-x-3 gap-y-0.5 ${def.classes.length > 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {def.classes.map((c) => (
        <div key={c.label} className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0 border border-white/20" style={{ backgroundColor: c.color }} />
          <span className="text-[9px] text-clay whitespace-nowrap">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function LegendPill({ legendKey, def }: { legendKey: string; def: LegendDef }) {
  const mdtRescale = useAtlasStore((s) => s.mdtRescale);

  let min: string | undefined;
  let max: string | undefined;
  if (def.type === 'gradient' && legendKey === 'mdt' && mdtRescale) {
    const [lo, hi] = mdtRescale.split(',');
    min = lo;
    max = hi;
  }

  return (
    <div className="bg-schist rounded-md px-3 py-2 shadow-lg shadow-black/20 border border-white/5">
      <div className="text-[10px] font-medium text-limestone uppercase tracking-wider mb-1.5">
        {def.label}
      </div>
      {def.type === 'gradient' && <GradientLegend def={def} min={min} max={max} />}
      {def.type === 'discrete' && <DiscreteLegend def={def} />}
      {def.type === 'lines' && <LineLegend def={def} />}
      {def.type === 'fills' && <FillLegend def={def} />}
    </div>
  );
}

// Map store visibility keys to legend config keys
const LEGEND_VISIBILITY: { storeKey: string; legendKey: string }[] = [
  { storeKey: 'mdtVisible', legendKey: 'mdt' },
  { storeKey: 'slopeVisible', legendKey: 'slope' },
  { storeKey: 'aspectVisible', legendKey: 'aspect' },
  { storeKey: 'geomorphonsVisible', legendKey: 'geomorphons' },
  { storeKey: 'windExpositionVisible', legendKey: 'windExposition' },
  { storeKey: 'erosionRiskVisible', legendKey: 'erosionRisk' },
  { storeKey: 'flowAccVisible', legendKey: 'flowAcc' },
  { storeKey: 'streamsVisible', legendKey: 'streams' },
  { storeKey: 'ridgesVisible', legendKey: 'ridges' },
  { storeKey: 'basinsVisible', legendKey: 'basins' },
  { storeKey: 'floodZonesVisible', legendKey: 'floodZones' },
  { storeKey: 'landuseVisible', legendKey: 'landuse' },
];

export function LegendStrip() {
  const state = useAtlasStore();

  const activeLegends = LEGEND_VISIBILITY
    .filter(({ storeKey }) => (state as any)[storeKey])
    .map(({ legendKey }) => ({ key: legendKey, def: LAYER_LEGENDS[legendKey] }))
    .filter(({ def }) => !!def);

  if (activeLegends.length === 0) return null;

  return (
    <div className="absolute bottom-8 right-4 z-10 flex flex-wrap-reverse gap-2 justify-end max-w-[60vw]">
      {activeLegends.map(({ key, def }) => (
        <LegendPill key={key} legendKey={key} def={def} />
      ))}
    </div>
  );
}
